import React, { useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Footer from './intake/Footer';
import BrandLogo from '@/components/BrandLogo';
import { useTenantBranding } from '@/context/TenantBranding';
import { buildTenantUrl } from '@/lib/tenant';
import useIntakeAssets from '@/hooks/useIntakeAssets';
import { assetAlt, assetUrl, landingCardKey } from '@/services/intakeAssets';

// Program cards for the "primary health goal" selector. Each entry renders one
// card with a full-bleed photo header. Drop a new image into public/programs/
// and point `image` at it to swap a card's photo.
//
// A super-admin can also swap these without a deploy: each card's `key` maps
// to the backend slot `landing.card.<key>`, and a published override wins over
// the bundled file (docs/INTAKE_PRD_ASSET_MANIFEST_READ_2026_09_27.md §3C).
// NOTE: `advanced-peptide` has no slot on the backend yet — it is not one of
// the nine seeded landing cards, so it can only be changed here.
const PROGRAMS = [
  {
    key: 'branded-weight-loss',
    route: '/branded-weight-loss',
    testid: 'branded-weight-loss-goal-btn',
    title: 'Branded Weight Loss',
    description: 'Brand-name GLP-1 medications, prescribed and shipped.',
    tags: ['Wegovy', 'Ozempic', 'Zepbound', 'Mounjaro'],
    tileClass: 'from-teal-50 to-emerald-100',
    tagClass: 'bg-teal-50 text-teal-700',
    image: '/programs/branded-weight-loss.png',
    imageAlt: 'Branded GLP-1 weight loss',
    icon: (
      <svg className="w-7 h-7 text-[#2d8a5f]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
      </svg>
    ),
  },
  {
    key: 'weight-loss',
    route: '/glp1',
    testid: 'weight-loss-goal-btn',
    title: 'Customized Weight Loss',
    description: 'Compounded GLP-1 medications for safe and effective weight loss.',
    tags: ['Semaglutide', 'Tirzepatide'],
    tileClass: 'from-green-50 to-emerald-100',
    tagClass: 'bg-green-50 text-green-700',
    image: '/programs/weight-loss.jpg',
    imageAlt: 'Before and after weight-loss transformation',
    icon: (
      <svg className="w-7 h-7 text-[#2d8a5f]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
      </svg>
    ),
  },
  {
    key: 'muscle-building',
    route: '/sermorelin',
    testid: 'muscle-building-goal-btn',
    title: 'Build Muscle',
    description: 'Metabolic support for muscle growth and energy.',
    // Tesamorelin hidden for now (2026-09-24); re-add 'Tesamorelin' here and
    // remove it from data/hiddenMedications.js to bring it back.
    tags: ['Sermorelin'],
    tileClass: 'from-blue-50 to-indigo-100',
    tagClass: 'bg-blue-50 text-blue-700',
    image: '/programs/muscle-building.jpg',
    imageAlt: 'Muscle building program',
    icon: (
      <svg className="w-7 h-7 text-[#2d8a5f]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
      </svg>
    ),
  },
  {
    key: 'anti-aging',
    route: '/anti-aging',
    testid: 'anti-aging-goal-btn',
    title: 'Anti-Aging',
    description: 'Cellular health and longevity treatments.',
    tags: ['NAD+', 'Glutathione', 'Metformin'],
    tileClass: 'from-purple-50 to-violet-100',
    tagClass: 'bg-purple-50 text-purple-700',
    image: '/programs/anti-aging.jpg',
    imageAlt: 'Anti-aging treatment',
    icon: (
      <svg className="w-7 h-7 text-[#2d8a5f]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
      </svg>
    ),
  },
  {
    key: 'trt',
    route: '/trt',
    testid: 'trt-goal-btn',
    title: 'Testosterone',
    description: 'TRT, enclomiphene, and HCG with lab integration.',
    tags: ['Testosterone', 'Enclomiphene', 'HCG'],
    tileClass: 'from-amber-50 to-orange-100',
    tagClass: 'bg-amber-50 text-amber-700',
    image: '/programs/trt.jpg',
    imageAlt: 'Testosterone therapy',
    icon: (
      <svg className="w-7 h-7 text-[#2d8a5f]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
      </svg>
    ),
  },
  {
    key: 'womens-sexual-health',
    route: '/womens-sexual-health',
    testid: 'womens-sexual-health-goal-btn',
    title: "Women's Sexual Health",
    description: 'Treatments to support desire, arousal, and intimacy.',
    tags: ['PT-141', 'Oxytocin'],
    tileClass: 'from-rose-50 to-pink-100',
    tagClass: 'bg-rose-50 text-rose-700',
    image: '/programs/womens-sexual-health.jpg',
    imageAlt: "Women's sexual health",
    icon: (
      <svg className="w-7 h-7 text-[#2d8a5f]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
      </svg>
    ),
  },
  {
    key: 'mens-sexual-health',
    route: '/mens-sexual-health',
    testid: 'mens-sexual-health-goal-btn',
    title: "Men's Sexual Health",
    description: 'Erectile function and libido support, discreetly delivered.',
    tags: ['Sildenafil', 'Tadalafil', 'PT-141'],
    tileClass: 'from-sky-50 to-cyan-100',
    tagClass: 'bg-sky-50 text-sky-700',
    image: '/programs/mens-sexual-health.jpg',
    imageAlt: "Men's sexual health",
    icon: (
      <svg className="w-7 h-7 text-[#2d8a5f]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
      </svg>
    ),
  },
  {
    key: 'advanced-peptide',
    route: '/advanced-peptide',
    // Public since 2026-09-29 (backend live, plans in Square). Set hidden: true
    // to pull the card without removing the route.
    testid: 'advanced-peptide-goal-btn',
    title: 'Concierge Advanced Peptide Program',
    description: 'Physician-guided peptide care with direct access to Dr. Mavroson. Monthly membership — New York.',
    tags: ['Peptides', '$299/mo', 'NY'],
    tileClass: 'from-violet-50 to-purple-100',
    tagClass: 'bg-violet-50 text-violet-700',
    image: '/programs/concierge-advanced-peptide.jpg',
    imageAlt: 'Concierge Advanced Peptide Program',
    icon: (
      <svg className="w-7 h-7 text-[#2d8a5f]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 3h6M10 3v4.5L5.6 16a3 3 0 002.6 4.5h7.6a3 3 0 002.6-4.5L14 7.5V3M7.5 13h9" />
      </svg>
    ),
  },
  {
    key: 'advanced-weight-loss',
    route: '/advanced-weight-loss',
    testid: 'advanced-weight-loss-goal-btn',
    title: 'Advanced Weight Loss Support',
    description: 'Physician-guided weight management with direct access to Dr. Mavroson. Monthly membership — New York.',
    tags: ['Weight loss', '$149/mo', 'NY'],
    tileClass: 'from-violet-50 to-purple-100',
    tagClass: 'bg-violet-50 text-violet-700',
    image: '/programs/concierge-weight-management.jpg',
    imageAlt: 'Concierge Weight Management Program',
    icon: (
      <svg className="w-7 h-7 text-[#2d8a5f]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
      </svg>
    ),
  },
  {
    key: 'advanced-longevity',
    route: '/advanced-longevity',
    testid: 'advanced-longevity-goal-btn',
    title: 'Advanced Longevity & Functional Medicine',
    description: 'Physician-guided healthy aging with functional-medicine labs and direct access to Dr. Mavroson. $199 first month, then $299/mo — New York.',
    tags: ['Longevity', '$199 then $299/mo', 'NY'],
    tileClass: 'from-violet-50 to-purple-100',
    tagClass: 'bg-violet-50 text-violet-700',
    image: '/programs/concierge-longevity.jpg',
    imageAlt: 'Concierge Longevity & Functional Medicine',
    icon: (
      <svg className="w-7 h-7 text-[#2d8a5f]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.5 12.75l6 6 9-13.5" />
      </svg>
    ),
  },
  {
    key: 'lab-panel',
    route: '/lab-panel',
    testid: 'lab-panel-goal-btn',
    title: 'Lab Testing',
    description: 'Comprehensive 60, 125 or 160-biomarker blood panel, provider-authorized — no requisition slip needed.',
    tags: ['60–160 biomarkers', 'From $299'],
    tileClass: 'from-red-50 to-rose-100',
    tagClass: 'bg-red-50 text-red-700',
    image: '/programs/lab-panel.png',
    imageAlt: 'Comprehensive lab blood panel',
    icon: (
      <svg className="w-7 h-7 text-[#2d8a5f]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z" />
      </svg>
    ),
  },
  {
    key: 'lab-analysis',
    route: '/lab-analysis',
    testid: 'lab-analysis-goal-btn',
    title: 'Lab Analysis',
    description: 'Already have lab results? A licensed provider reviews them and shares personalized wellness feedback.',
    tags: ['Results review', '$99'],
    tileClass: 'from-sky-50 to-cyan-100',
    tagClass: 'bg-sky-50 text-sky-700',
    image: '/programs/lab-analysis.jpg',
    imageAlt: 'Doctor reviewing lab results with a patient',
    imagePosition: '50% 46%', // portrait source — frame both faces + the desk/paper

    icon: (
      <svg className="w-7 h-7 text-[#2d8a5f]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
      </svg>
    ),
  },
];

const LandingPage = () => {
  const navigate = useNavigate();
  const { dashboardUrl } = useTenantBranding();
  const { assets } = useIntakeAssets();
  const trackRef = useRef(null);

  // Page the carousel by ~one card width (snap handles final alignment).
  const scrollByCards = useCallback((dir) => {
    const el = trackRef.current;
    if (!el) return;
    const card = el.querySelector('[data-card]');
    const step = card ? card.offsetWidth + 20 /* gap */ : el.clientWidth * 0.8;
    el.scrollBy({ left: dir * step, behavior: 'smooth' });
  }, []);

  const onTrackKeyDown = useCallback(
    (e) => {
      if (e.key === 'ArrowRight') {
        e.preventDefault();
        scrollByCards(1);
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault();
        scrollByCards(-1);
      }
    },
    [scrollByCards],
  );

  return (
    // Natural page scrolling at every size. The page used to lock itself to
    // one viewport on lg+ (h-screen + overflow-hidden); once the footer grew
    // (LegitScript seal) the locked page no longer fit a 1280×850 window and
    // the header, cards, sign-in pill and footer all painted over one another
    // on production (owner screenshot 2026-10-11 00:42). A flex column with
    // min-h-screen keeps the content centered when there is room and simply
    // scrolls when there isn't.
    <div
      className="min-h-screen flex flex-col bg-white"
      data-testid="landing-page"
    >
      {/* Header */}
      <header className="w-full px-4 pt-6 pb-3 lg:pt-5 lg:pb-5">
        <div className="max-w-5xl mx-auto text-center">
          <BrandLogo
            alt="FitPal"
            className="mx-auto mb-3 w-56 sm:w-64 lg:w-56 h-auto"
          />
          <p className="text-gray-500 text-sm">Your personalized health journey starts here</p>
        </div>
      </header>

      {/* Main Content */}
      {/* `my-auto` on the content block centers it vertically when the
          viewport is taller than the page; when it isn't, the page scrolls —
          nothing is clipped and nothing overlaps. */}
      <main className="flex-1 flex flex-col px-4 py-2 lg:py-4">
        <div className="w-full max-w-5xl mx-auto my-auto pb-4">
          {/* Question */}
          <div className="text-center mb-5 lg:mb-6 lg:mt-4">
            <h1 className="text-2xl md:text-3xl lg:text-3xl font-semibold text-gray-900 mb-2 lg:mb-3">
              What is your primary health goal?
            </h1>
            <p className="text-gray-500 text-sm md:text-base">
              Select the option that best describes what you want to achieve
            </p>
          </div>

          {/* Goal Options — horizontal scroll-snap carousel. One row keeps the
              desktop view within a single viewport; swipe on touch, arrows on
              pointer devices, ←/→ keys when the track is focused. */}
          <div className="relative">
            {/* Prev arrow (pointer devices) */}
            <button
              type="button"
              onClick={() => scrollByCards(-1)}
              aria-label="Previous programs"
              className="hidden sm:flex absolute -left-3 lg:-left-5 top-1/2 -translate-y-1/2 z-20 w-11 h-11 items-center justify-center rounded-full bg-white border border-gray-200 shadow-md text-gray-700 hover:text-[#2d8a5f] hover:border-[#2d8a5f] focus:outline-none focus:ring-2 focus:ring-[#2d8a5f]/50 transition-colors"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>

            {/* Track */}
            <div
              ref={trackRef}
              onKeyDown={onTrackKeyDown}
              role="region"
              aria-label="Treatment programs — scroll to browse"
              tabIndex={0}
              className="flex gap-5 overflow-x-auto snap-x snap-mandatory scroll-smooth pb-3 px-1 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#2d8a5f]/40 rounded-2xl [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
            >
              {PROGRAMS.filter((p) => !p.hidden).map((program) => (
                <button
                  key={program.key}
                  data-card
                  onClick={() => navigate(buildTenantUrl(program.route))}
                  aria-label={`${program.title} — ${program.description}`}
                  className="group relative snap-start shrink-0 w-[78vw] sm:w-[300px] lg:w-[280px] flex flex-col bg-white border-2 border-gray-200 rounded-2xl overflow-hidden text-left hover:border-[#2d8a5f] hover:shadow-xl hover:-translate-y-1 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#2d8a5f] transition-all duration-300"
                  data-testid={program.testid}
                >
                  {/* Selection indicator (gets a backdrop when it sits over a photo) */}
                  <div className="absolute top-3 right-3 z-10">
                    <div
                      className={`w-6 h-6 rounded-full border-2 border-gray-300 group-hover:border-[#2d8a5f] transition-colors ${
                        program.image ? 'bg-white/90 backdrop-blur-sm shadow-sm' : ''
                      }`}
                    ></div>
                  </div>

                  {/* Photo header (optional) — larger */}
                  {program.image ? (
                    <div className="relative w-full h-44 sm:h-48 lg:h-44 overflow-hidden">
                      <img
                        src={assetUrl(assets, landingCardKey(program.key), program.image)}
                        alt={assetAlt(
                          assets,
                          landingCardKey(program.key),
                          program.imageAlt || program.title,
                        )}
                        style={program.imagePosition ? { objectPosition: program.imagePosition } : undefined}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/10 to-transparent" />
                    </div>
                  ) : (
                    // Icon-tile header for cards without a photo (keeps a
                    // consistent tall header height across the carousel).
                    <div
                      className={`w-full h-44 sm:h-48 lg:h-44 bg-gradient-to-br ${program.tileClass} flex items-center justify-center`}
                    >
                      <div className="w-16 h-16 bg-white/70 backdrop-blur-sm rounded-2xl flex items-center justify-center shadow-sm group-hover:scale-110 transition-transform">
                        {program.icon}
                      </div>
                    </div>
                  )}

                  {/* Body */}
                  <div className="p-5 flex flex-col flex-1">
                    <h3 className="text-lg font-semibold text-gray-900 mb-1 group-hover:text-[#2d8a5f] transition-colors">
                      {program.title}
                    </h3>
                    <p className="text-gray-500 text-sm mb-4 leading-snug">{program.description}</p>

                    {/* Tags */}
                    <div className="mt-auto flex flex-wrap gap-1.5">
                      {program.tags.map((tag) => (
                        <span
                          key={tag}
                          className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${program.tagClass}`}
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                </button>
              ))}
            </div>

            {/* Next arrow (pointer devices) */}
            <button
              type="button"
              onClick={() => scrollByCards(1)}
              aria-label="Next programs"
              className="hidden sm:flex absolute -right-3 lg:-right-5 top-1/2 -translate-y-1/2 z-20 w-11 h-11 items-center justify-center rounded-full bg-white border border-gray-200 shadow-md text-gray-700 hover:text-[#2d8a5f] hover:border-[#2d8a5f] focus:outline-none focus:ring-2 focus:ring-[#2d8a5f]/50 transition-colors"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </div>

          {/* Existing Patient Link — routes to dashboard, not the public
              follow-up form. Per §0.A of the embed-wrapper PRD, all
              follow-ups must originate from inside the patient dashboard. */}
          <div className="mt-5 lg:mt-4 text-center">
            <a
              href={dashboardUrl}
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl transition-colors font-medium text-sm"
              data-testid="dashboard-login-link"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 16l-4-4m0 0l4-4m-4 4h14m-5 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h7a3 3 0 013 3v1" />
              </svg>
              Already a patient? Sign in
            </a>
          </div>
        </div>
      </main>

      {/* Footer — never shrinks, never overlaps main. */}
      <div className="shrink-0">
        <Footer />
      </div>
    </div>
  );
};

export default LandingPage;
